import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Globe, Copy, Check, XCircle, Download } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { API } from "../../context/AuthContext";

export default function PublicPanel({ caseData, onEventAdded, onCaseUpdated }) {
  const [tips, setTips] = useState([]);
  const qrRef = useRef(null);
  const isPublic = caseData?.is_public;

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `flux-tipline-qr-${caseData.public_slug}.png`;
    a.click();
  };

  const loadTips = useCallback(() => {
    if (!caseData?.is_public) return;
    axios.get(`${API}/cases/${caseData.case_id}/tips`).then((res) => setTips(res.data)).catch(() => {});
  }, [caseData]);

  useEffect(() => { setTips([]); loadTips(); }, [loadTips]);

  if (!caseData) return null;

  const toggle = async () => {
    try {
      const res = await axios.post(`${API}/cases/${caseData.case_id}/public`, { enabled: !isPublic });
      onCaseUpdated({ ...caseData, is_public: res.data.is_public, public_slug: res.data.public_slug });
      if (res.data.is_public) {
        navigator.clipboard.writeText(`${window.location.origin}/t/${res.data.public_slug}`);
        toast.success("Timeline is now PUBLIC — link copied! Anyone can view it and submit tips.");
      } else {
        toast.success("Timeline is private again. The public link no longer works.");
      }
    } catch {
      toast.error("Couldn't update public status.");
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/t/${caseData.public_slug}`);
    toast.success("Public link copied!");
  };

  const moderate = async (tipId, action) => {
    try {
      const res = await axios.post(`${API}/tips/${tipId}/${action}`);
      setTips((prev) => prev.filter((t) => t.tip_id !== tipId));
      if (action === "approve") {
        onEventAdded(res.data);
        toast.success("Tip approved — it's on the timeline now.");
      } else {
        toast.success("Tip rejected.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't moderate that tip.");
    }
  };

  return (
    <div className="bg-[#131f3a] border border-white/5 p-5 rounded-2xl shadow-xl space-y-3" data-testid="public-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Globe className={`w-4 h-4 ${isPublic ? "text-emerald-400" : "text-gray-500"}`} />
          <h4 className="text-xs font-bold uppercase tracking-wider text-gray-300">Public Timeline</h4>
        </div>
        <button
          onClick={toggle}
          data-testid="public-toggle-btn"
          className={`text-[10px] font-mono font-bold px-2.5 py-1 rounded-lg border transition-colors duration-200 ${isPublic ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/5" : "text-gray-400 border-white/10 hover:text-emerald-400 hover:border-emerald-500/40"}`}
        >
          {isPublic ? "PUBLIC — ON" : "MAKE PUBLIC"}
        </button>
      </div>
      <p className="text-[11px] text-gray-400 leading-relaxed">
        For missing persons and community investigations: anyone with the link can view this timeline and submit tips. You are the admin — tips only appear after you approve them.
      </p>
      {isPublic && (
        <>
          <button onClick={copyLink} data-testid="public-copy-link-btn" className="flex items-center gap-1.5 text-[10px] font-mono text-[#2dd4bf] border border-[#2dd4bf]/30 hover:border-[#2dd4bf]/60 px-2.5 py-1.5 rounded-lg transition-colors duration-200 w-full justify-center">
            <Copy className="w-3 h-3" /> COPY PUBLIC LINK
          </button>
          <div className="flex items-center gap-3 bg-gray-950 border border-white/5 rounded-xl p-3" data-testid="public-qr-panel">
            <div className="bg-white p-1.5 rounded-lg" ref={qrRef}>
              <QRCodeCanvas value={`${window.location.origin}/t/${caseData.public_slug}`} size={72} fgColor="#0f1729" bgColor="#ffffff" />
            </div>
            <div className="space-y-1.5">
              <p className="text-[10px] text-gray-400 leading-snug">Print this QR on flyers and posters — anyone who scans it can view the timeline and submit a tip.</p>
              <button onClick={downloadQr} data-testid="public-qr-download-btn" className="flex items-center gap-1 text-[10px] font-mono text-[#f59e0b] hover:text-[#fbbf24] transition-colors duration-200">
                <Download className="w-3 h-3" /> DOWNLOAD QR (PNG)
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <h5 className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
              Tip Queue {tips.length > 0 && <span className="text-[#f59e0b]" data-testid="tip-count">({tips.length} pending)</span>}
            </h5>
            {tips.length === 0 ? (
              <p className="text-[11px] text-gray-500 italic" data-testid="tips-empty">No pending tips.</p>
            ) : (
              tips.map((t) => (
                <div key={t.tip_id} className="bg-gray-950 border border-white/5 rounded-xl p-3 space-y-1.5" data-testid={`tip-row-${t.tip_id}`}>
                  <div className="flex justify-between items-center text-[10px] font-mono">
                    <span className="text-[#f59e0b]">{t.date}</span>
                    <span className="text-gray-500">{t.name || "Anonymous"}{t.contact ? ` · ${t.contact}` : ""}</span>
                  </div>
                  <div className="text-xs text-gray-200 font-semibold">{t.title}</div>
                  {t.description && <p className="text-[11px] text-gray-400">{t.description}</p>}
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => moderate(t.tip_id, "approve")} data-testid={`tip-approve-${t.tip_id}`} className="flex items-center gap-1 text-[10px] font-mono font-bold text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/10 px-2 py-1 rounded-lg transition-colors duration-200">
                      <Check className="w-3 h-3" /> APPROVE
                    </button>
                    <button onClick={() => moderate(t.tip_id, "reject")} data-testid={`tip-reject-${t.tip_id}`} className="flex items-center gap-1 text-[10px] font-mono text-gray-400 border border-white/10 hover:text-rose-400 hover:border-rose-500/30 px-2 py-1 rounded-lg transition-colors duration-200">
                      <XCircle className="w-3 h-3" /> REJECT
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
